import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  cardSx, headCellSx, EmployeeSelect, CURRENT_YEAR, getYearOptions,
} from "../../../components/leave/LeaveCommon";
import { fetchLeaveSummary } from "../../../actions/Leave/LeaveAllocationAction";
import { toEmployeeOptions } from "../../../service/Leave/leaveHelpers";

const ROWS = 8;
const f1 = (n) => Number(n || 0).toFixed(1);

const LeaveBalanceTab = () => {
  const dispatch = useDispatch();
  const { data: rawEmployees } = useSelector((s) => s.getAllEmployees) || {};
  const { summary = [], summaryLoading, summaryError } = useSelector((s) => s.leaveAllocations) || {};

  const [employee, setEmployee] = useState(null); // null = all employees
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [page, setPage] = useState(0);

  const employees = useMemo(() => toEmployeeOptions(rawEmployees), [rawEmployees]);

  useEffect(() => { dispatch(fetchLeaveSummary()); }, [dispatch]);

  const yearRows = useMemo(() => summary.filter((r) => r.year === year), [summary, year]);

  // one column per leave type that appears in the chosen year
  const typeCols = useMemo(() => {
    const map = new Map();
    yearRows.forEach((r) => { if (!map.has(r.typeCode)) map.set(r.typeCode, r.typeName || r.typeCode); });
    return Array.from(map, ([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [yearRows]);

  // one row per employee
  const grouped = useMemo(() => {
    const map = new Map();
    yearRows.forEach((r) => {
      if (!map.has(r.serviceNo)) {
        map.set(r.serviceNo, { serviceNo: r.serviceNo, employeeName: r.employeeName || r.serviceNo, types: {}, totalUsed: 0, totalBalance: 0 });
      }
      const e = map.get(r.serviceNo);
      e.types[r.typeCode] = r;
      e.totalUsed += r.used;
      e.totalBalance += r.balance;
    });
    return Array.from(map.values()).sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }, [yearRows]);

  const filtered = grouped.filter((r) => !employee || r.serviceNo === employee.serviceNo);
  const rows = filtered.slice(page * ROWS, page * ROWS + ROWS);

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, alignItems: "center" }}>
        <EmployeeSelect
          label="Employee" placeholder="All Employees" options={employees} value={employee}
          onChange={(e) => { setEmployee(e); setPage(0); }} sx={{ width: 320 }}
        />
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Year</InputLabel>
          <Select value={year} label="Year" onChange={(e) => { setYear(e.target.value); setPage(0); }}>
            {getYearOptions().map((y) => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <Button
          onClick={() => {
            setEmployee(null);
            setYear(String(CURRENT_YEAR));
            setPage(0);
            dispatch(fetchLeaveSummary());
          }}
          disabled={summaryLoading}
          startIcon={<RefreshIcon sx={summaryLoading ? { animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } } : {}} />}
          sx={{ textTransform: "none", fontWeight: 600, color: "#334155", border: "1px solid #e2e8f0", height: 40, px: 2 }}
        >
          Refresh
        </Button>
      </Box>

      {summaryError && <Alert severity="error" sx={{ mb: 2 }}>{summaryError}</Alert>}

      <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Employee</TableCell>
                <TableCell sx={headCellSx}>Service No</TableCell>
                <TableCell sx={headCellSx} align="center">Year</TableCell>
                {typeCols.map((t) => (
                  <TableCell key={t.code} sx={headCellSx} align="center">{t.name}</TableCell>
                ))}
                <TableCell sx={headCellSx} align="center">Total Used</TableCell>
                <TableCell sx={headCellSx} align="center">Total Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaryLoading && summary.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5 + typeCols.length} align="center" sx={{ py: 5 }}><CircularProgress size={24} /></TableCell>
                </TableRow>
              )}
              {!summaryLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5 + typeCols.length} align="center" sx={{ py: 5, color: "#94a3b8" }}>
                    No leave balances found.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.serviceNo} hover>
                  <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{r.employeeName}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: "#64748b" }}>{r.serviceNo}</TableCell>
                  <TableCell align="center" sx={{ fontSize: 13, color: "#64748b" }}>{year}</TableCell>
                  {typeCols.map((t) => {
                    const a = r.types[t.code];
                    return (
                      <TableCell key={t.code} align="center" sx={{ fontSize: 13 }}>
                        {a ? (
                          <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: a.balance <= 0 ? "#dc2626" : "#059669" }}>
                            {f1(a.balance)}
                          </Typography>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center" sx={{ fontSize: 13, color: "#475569" }}>{f1(r.totalUsed)}</TableCell>
                  <TableCell align="center" sx={{ fontSize: 13, fontWeight: 700, color: "#059669" }}>{f1(r.totalBalance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={filtered.length} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={ROWS} rowsPerPageOptions={[ROWS]}
        />
      </Paper>
      <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 1 }}>
        Balances are remaining days per leave type. A dash means the type is not allocated.
      </Typography>
    </Box>
  );
};

export default LeaveBalanceTab;